import { isFocusable } from 'tabbable'

import { findClosestFocusable } from './focus'
import * as platform from './platform'
import * as vec from './vector'

/**
 * A temporary solution to preventing fullscreen from eating events on our elements. We should
 * eventually figure out how to do this from outside of fpl.
 * https://app.asana.com/0/0/1207289930014758/f
 */
export const PREVENT_EVENT_CAPTURE_ATTR = 'data-fullscreen-prevent-event-capture'
export const PREVENT_EVENT_CAPTURE = Object.freeze({ [PREVENT_EVENT_CAPTURE_ATTR]: 'true' })

export const PREVENT_KEY_CAPTURE_ATTR = 'data-fullscreen-prevent-event-capture-keys'
export const PREVENT_KEY_CAPTURE = Object.freeze({ [PREVENT_KEY_CAPTURE_ATTR]: 'true' })

export const PREVENT_WHEEL_CAPTURE_ATTR = 'data-fullscreen-wheel-event-capture'
export const PREVENT_WHEEL_CAPTURE = Object.freeze({ [PREVENT_WHEEL_CAPTURE_ATTR]: 'true' })

export const WHEEL_EVENT_PASSTHROUGH_ATTR = 'data-fullscreen-wheel-event-passthrough'
export const WHEEL_EVENT_PASSTHROUGH = Object.freeze({ [WHEEL_EVENT_PASSTHROUGH_ATTR]: 'true' })

/**
 * The names of the values the browser assigns the `button` property of mouse and pointer events.
 */
export const enum MouseButton {
  LEFT = 0,
  MIDDLE = 1,
  RIGHT = 2,
  BACK = 3,
  FORWARD = 4,
}

/**
 * Use our terminology for event handling - accept means you have decided to handle the event and
 * expect nothing else to come of it.
 *
 * TODO: should only certain events be acceptable? For example, should it be considered a bug
 * to accept a `pointermove` event. So far I have only been using this for keyboard events.
 */
export function accept(e: React.SyntheticEvent | Event) {
  e.preventDefault()
  e.stopPropagation()
}

/**
 * Intended to be called as `if (forward(e, cb)) return`. Calls the callback with the event,
 * and returns whether the default was prevented.
 */
export function forward<T extends React.SyntheticEvent | Event>(
  e: T,
  cb: ((e: T) => void) | null | undefined,
) {
  cb?.(e)
  return e.defaultPrevented
}

export function preventDefault(e: React.SyntheticEvent | Event) {
  e.preventDefault()
}

export function stopPropagation(e: React.SyntheticEvent | Event) {
  e.stopPropagation()
}

/**
 * Check if a click event was triggered by a keyboard. Keyboard clicks have their `clientX` and
 * `clientY` values set to 0.
 */
export function isKeyboardClick(e: React.MouseEvent | MouseEvent) {
  return e.clientX === 0 && e.clientY === 0
}

/**
 * Check if the event's `target` is the same as the `currentTarget`. This can be used
 * to determine if the event was triggered on the element that the event listener was
 * attached to, vs. a descendant element.
 */
export function targetIsCurrent(e: React.SyntheticEvent | Event) {
  return e.target === e.currentTarget
}

/**
 * Determines if the event came through a react portal by checking if the
 * target is not a descendant of the element the event listener is attached to.
 */
export function isPortaledEvent(e: React.SyntheticEvent | Event) {
  return !(e.currentTarget as HTMLElement).contains(e.target as HTMLElement)
}

/** Anything with `addEventListener`/`removeEventListener` and a known event map. */
export type Listenable = Window | Document | HTMLElement | MediaQueryList | AbortSignal

/** Map a listenable target to its event map. */
export type EventMapOf<T> = T extends Window
  ? WindowEventMap
  : T extends Document
    ? DocumentEventMap
    : T extends MediaQueryList
      ? MediaQueryListEventMap
      : T extends AbortSignal
        ? AbortSignalEventMap
        : T extends HTMLElement
          ? HTMLElementEventMap
          : never

/**
 * A function which attaches the requested event listener `type` to the given `el` and returns
 * a function that removes the listener. Simplifies the common pattern of adding and removing
 * event listeners within React effects, as you can simply return the result of this function.
 *
 * When called with a non-nullable `el`, the return type is a non-undefined cleanup function.
 * When called with a possibly-null/undefined `el`, the return type is `(() => void) | undefined`
 * (the listener was not attached when `el` was nullish).
 *
 * @example
 *
 * ```ts
 * useEffect(() => addListener(window, 'resize', () => console.log('resized')))
 * ```
 */
export function addListener<T extends Listenable, K extends keyof EventMapOf<T>>(
  el: T,
  type: K,
  listener: (this: T, e: EventMapOf<T>[K]) => void,
  options?: boolean | AddEventListenerOptions,
): () => void
export function addListener<T extends Listenable, K extends keyof EventMapOf<T>>(
  el: T | null | undefined,
  type: K,
  listener: (this: T, e: EventMapOf<T>[K]) => void,
  options?: boolean | AddEventListenerOptions,
): (() => void) | undefined
export function addListener(
  el: Listenable | null | undefined,
  type: string,
  listener: (e: any) => void,
  options?: boolean | AddEventListenerOptions,
) {
  if (el) {
    el.addEventListener(type, listener, options)
    return () => el.removeEventListener(type, listener, options)
  }
}

/**
 * Check if a `pointerdown` event was triggered by a screen reader click (VO + space).
 *
 * @note This *only* works for `pointerdown` events, `pointerup` events always have 0 width/height.
 *
 * Borrowed from @react-aria/utils and modified.
 * @see https://github.com/adobe/react-spectrum/blob/da4ef76444b9957d1dc18fc36b4ed9405c68f7fb/packages/%40react-aria/utils/src/isVirtualEvent.ts#L42
 *
 * TODO: figure out how to provide attribution properly with license etc.
 * https://figma.slack.com/archives/C08A57KUVA9/p1756312808207749
 */
export function isVirtualPointerDownEvent(event: React.PointerEvent | PointerEvent) {
  // don't classify test pointerdown events as virtual
  if (process.env.NODE_ENV === 'test') return false

  const e = asNativeEvent(event)

  if (platform.vitest) {
    // react testing library doesn't have a way to trigger a click at a specific position
    // and pretty much always trips the virtual click logic. During tests we can force this
    // function to return whatever is needed for the test, and at build time this is eliminated.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (window as any).FPL_FORCE_VIRTUAL_CLICKS_FOR_TESTING as boolean
  }

  // In MacOS VoiceOver we can only really use the center clicking to determine if
  // a user is a voice over user or not.
  //
  // Also use this logic for Playwright so that tests are able to simulate virtual vs. real
  // pointerdown events by passing a `position` other than the center of the target.
  if ((platform.mac || platform.playwright) && e.target instanceof Element) {
    const client = vec.fromClientXY(e)

    // VoiceOver clicks (ctrl+opt+space) are always integer values, while an actual
    // pointing device will almost always have a long fractional value.
    if (!vec.isInteger(client)) return false

    let el: Element | null = e.target

    while (el) {
      const center = vec.fromElementCenter(el)

      // VoiceOver can sometimes use the floor or ceiling value seemingly based on the aspect ratio
      // of the element. So we ensure that it's both larger than the floor and smaller than the ceil.
      if (vec.isWithinRoundingDistance(client, center)) return true

      // Also try the first focusable ancestor, screen reader clicks sometimes set a child element
      // as the target even though they compute the center of the first focusable ancestor.
      el = isFocusable(el) ? null : findClosestFocusable(el)
    }

    return false
  }

  return (
    (!platform.android && e.width === 0 && e.height === 0) ||
    // Android TalkBack double tap sometimes returns an event with width and height of 1 and
    // pointerType === 'mouse' so we need to check for a specific combination of event attributes.
    (e.width === 1 &&
      e.height === 1 &&
      // Cannot use "event.pressure === 0" alone due to Safari pointer events always returning
      // pressure === 0 instead of .5, see https://bugs.webkit.org/show_bug.cgi?id=206216.
      e.pressure === 0 &&
      e.detail === 0 &&
      // distinguishes Talkback double tap from Windows Firefox touch screen press
      e.pointerType === 'mouse')
  )
}

const UNHANDLED_EVENTS = new WeakSet<Event>()

/**
 * Mark an event as unhandled. This is meant for events that have native browser behavior on a
 * specific target element, but that we want to mark as _not_ handled by the browser. For example,
 * the browser normally handles ⌘Z on inputs, but unmodified inputs may want to mark this event as
 * unhandled, which can inform a caller that they are allowed to handle the event.
 */
export function unhandle(e: React.SyntheticEvent | Event) {
  UNHANDLED_EVENTS.add(asNativeEvent(e))
}

/**
 * Check if an event has been marked as unhandled. This means the event would normally
 * be inferred as handled by the browser, but it was explicitly marked as unhandled.
 */
export function isEventUnhandled(e: React.SyntheticEvent | Event) {
  return UNHANDLED_EVENTS.has(asNativeEvent(e))
}

type AsNativeEvent<T extends React.SyntheticEvent | Event> =
  T extends React.SyntheticEvent<any, infer E> ? E : T

export function asNativeEvent<T extends React.SyntheticEvent | Event>(e: T): AsNativeEvent<T> {
  return ('nativeEvent' in e ? e.nativeEvent : e) as AsNativeEvent<T>
}

/**
 * Install a global Escape key listener that calls the provided callback when Escape is pressed.
 * Returns a cleanup function that removes the listener. The event is accepted by default, unless
 * `false` is returned from the callback. The callback is in capture phase to give priority to this
 * newly registered listener.
 */
export function handleEscape(onEscape: () => void | null | undefined | boolean): () => void {
  function listener(e: KeyboardEvent) {
    if (e.key === 'Escape' && onEscape() !== false) accept(e)
  }

  return addListener(document, 'keydown', listener, true)
}
